import { HStack, PageContainer, PageHeader, VStack } from '@/components/Atoms'
import { Block } from '@/components/Block'
import { Card, CardContent } from '@/components/Card'
import { ExportCsvMenu, ExportCsvMenuItem } from '@/components/ExportCsv/ExportCsvMenu'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { Note } from '@/components/Note'
import { PendingChanges } from '@/components/PendingChanges'
import { useCrtVersionSelectionSync } from '@/hooks/crt/useCrtVersionSelectionSync'
import {
  KeyAccountContractEstimateRow,
  keyAccountContractEstimatesExportQueryOptions,
  keyAccountContractEstimatesWithRatiosExportQueryOptions,
  useKeyAccountContractEstimates,
  useUpdateKeyAccountContractEstimates,
} from '@/hooks/queries/crt/useKeyAccountContractEstimates'
import { queryClient } from '@/main'
import { formatDateOnly } from '@/utils/DateOnlyHelpers'
import { UpdateEstimates } from '@repo/backend/crt'
import { ContractClause, PRODUCT_CATEGORIES, ProductCategory } from '@repo/constants'
import { Button } from '@repo/ui/components'
import { monthStartString } from '@repo/zod-schemas'
import { Link, createFileRoute } from '@tanstack/react-router'
import { startOfMonth } from 'date-fns'
import { ArrowLeftIcon, InfoIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import {
  type AssumptionSelection,
  toAssumptionBadges,
  toSelectionSearch,
} from '../../../-components/assumptions/assumption-selection'
import { ConfigureAssumptions } from '../../../-components/assumptions/ConfigureAssumptions'
import { EditKeyAccountDialog } from '../-components/EditKeyAccountDialog'
import { EditPerformanceRatiosDialog } from '../-components/EditPerformanceRatiosDialog'
import { AddKeyAccountDialog } from './-components/AddKeyAccountDialog'
import { EstimatesEmptyState } from './-components/EstimatesEmptyState'
import { EstimateCategoryKey, EstimatesTable, LocalEdits } from './-components/EstimatesTable'
import { UploadKeyAccountEstimatesDataDialog } from './-components/UploadKeyAccountEstimatesDataDialog'

const estimatesSearchSchema = z.object({
  productCategory: z.enum(PRODUCT_CATEGORIES).optional(),
  crtPeriod: monthStartString.optional(),
  // Exactly these manual investments count toward the accruals (none when empty), seeded from the CRT
  // version loaded in the header.
  loadedVersionId: z.number().optional(),
  manualInvestmentIds: z.array(z.number()).optional(),
})

const DEFAULT_PRODUCT_CATEGORY: ProductCategory = 'FMC'

const CONTRACT_CLAUSE_BY_FIELD = {
  conditional: 'Conditional',
  performance: 'P4P',
  contractSla: 'SLA',
} as const satisfies Record<EstimateCategoryKey, ContractClause>

export const Route = createFileRoute(
  '/_auth/_modules/crt/customer-investments/key-accounts/estimates/',
)({
  component: RouteComponent,
  validateSearch: (search) => estimatesSearchSchema.parse(search),
})

function RouteComponent() {
  const { t } = useTranslation(['crt', 'common'])

  const rawSearch = Route.useSearch()
  const navigate = Route.useNavigate()

  const { crtPeriod: selectedCrtPeriodStr } = useCrtVersionSelectionSync({
    search: rawSearch,
    navigate: (search) => navigate({ search }),
    fields: ['manualInvestmentIds'],
  })
  const [isConfigOpen, setIsConfigOpen] = useState(false)
  const configuration = { manualInvestmentIds: rawSearch.manualInvestmentIds }
  const configBadges = toAssumptionBadges(t, ['manual'], configuration)
  const handleApplyConfig = (selection: AssumptionSelection) => {
    navigate({ search: { ...rawSearch, ...toSelectionSearch(['manual'], selection) } })
  }
  const currentPeriodStr = formatDateOnly(startOfMonth(new Date()))
  const isPastPeriod = selectedCrtPeriodStr < currentPeriodStr

  const search = {
    productCategory: rawSearch.productCategory ?? DEFAULT_PRODUCT_CATEGORY,
    crtPeriod: selectedCrtPeriodStr,
    manualInvestmentIds: rawSearch.manualInvestmentIds,
  }

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [isRatiosDialogOpen, setIsRatiosDialogOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<{
    row: KeyAccountContractEstimateRow
    canDelete: boolean
  } | null>(null)
  // Custom state for edits instead of using react-hook-form to avoid reset not working as expected
  const [localEdits, setLocalEdits] = useState<LocalEdits>({})

  const { data, isPending, error } = useKeyAccountContractEstimates(search)

  const rows = (data?.results ?? []) as KeyAccountContractEstimateRow[]

  const { mutate: updateEstimates, isPending: isSaving } = useUpdateKeyAccountContractEstimates({
    handleOnSuccess: () => {
      setLocalEdits({})
    },
  })

  const handleCommitCell = (
    hierarchyErp: string,
    category: ProductCategory,
    field: EstimateCategoryKey,
    next: string,
  ) => {
    const row = rows.find((r) => r.hierarchyErp === hierarchyErp)
    const serverValue = row?.[field]
    const serverString =
      serverValue === null || serverValue === undefined ? '' : String(serverValue)

    // Pristine check: typed value numerically equals the row's server
    // snapshot. If the server value was null (serverString=''), only the
    // literal empty string counts as a no-op — typing "0" is an intentional
    // zero and still counts as an edit.
    const isPristine =
      serverString === '' ? next === '' : Number(next === '' ? '0' : next) === Number(serverString)

    setLocalEdits((prev) => {
      if (isPristine) {
        // Drop this leaf — and any now-empty parents — so the row returns to clean.
        const nextRow = { ...(prev[hierarchyErp] ?? {}) }
        const nextCategory = { ...(nextRow[category] ?? {}) }
        delete nextCategory[field]
        const updated: LocalEdits = { ...prev }
        if (Object.keys(nextCategory).length === 0) {
          delete nextRow[category]
        } else {
          nextRow[category] = nextCategory
        }
        if (Object.keys(nextRow).length === 0) {
          delete updated[hierarchyErp]
        } else {
          updated[hierarchyErp] = nextRow
        }
        return updated
      }

      return {
        ...prev,
        [hierarchyErp]: {
          ...prev[hierarchyErp],
          [category]: {
            ...prev[hierarchyErp]?.[category],
            [field]: next,
          },
        },
      }
    })
  }

  const handleClearRow = (hierarchyErp: string) => {
    setLocalEdits((prev) => {
      if (!prev[hierarchyErp]) return prev
      const next = { ...prev }
      delete next[hierarchyErp]
      return next
    })
  }

  const handleSaveChanges = () => {
    const updates: UpdateEstimates['updates'] = []
    for (const hierarchyErp of Object.keys(localEdits)) {
      const byCategory = localEdits[hierarchyErp] ?? {}
      for (const productCategory of Object.keys(byCategory) as ProductCategory[]) {
        const byField = byCategory[productCategory] ?? {}
        for (const field of Object.keys(byField) as EstimateCategoryKey[]) {
          const value = byField[field]
          if (value === undefined) continue
          updates.push({
            hierarchyErp,
            productCategory,
            contractClause: CONTRACT_CLAUSE_BY_FIELD[field],
            amount: Number(value === '' ? '0' : value),
          })
        }
      }
    }
    if (updates.length === 0) return
    updateEstimates({ crtPeriod: selectedCrtPeriodStr, updates })
  }

  const handleProductCategoryChange = (value: ProductCategory) => {
    if (value === search.productCategory) return
    setLocalEdits({})
    navigate({ search: { ...rawSearch, productCategory: value } })
  }

  // The period now moves from the global header selector; drop any in-progress edits when it changes,
  // since estimates are keyed to a single period.
  useEffect(() => {
    setLocalEdits({})
  }, [selectedCrtPeriodStr])

  const hasUnsaved = Object.keys(localEdits).length > 0

  const isEmpty = !isPending && rows.length === 0

  if (error) {
    return (
      <PageContainer>
        <PageHeader>{t('keyAccountContractEstimates.pageHeader')}</PageHeader>
        <HStack className="justify-center py-12">
          <span>{t('errors.generic', { ns: 'common' })}</span>
        </HStack>
      </PageContainer>
    )
  }

  return (
    <PageContainer className="pb-28">
      <Block shouldBlock={hasUnsaved} />

      <VStack gap="md" align="stretch">
        <HStack gap="sm" className="items-center justify-between">
          <PageHeader>{t('keyAccountContractEstimates.pageHeader')}</PageHeader>
        </HStack>
        <HStack gap="sm" className="items-center justify-end">
          <ExportCsvMenu
            items={[
              <ExportCsvMenuItem
                key="raw"
                label={t('keyAccountContractEstimates.export.raw')}
                description={t('keyAccountContractEstimates.export.rawDescription')}
                getCsvExport={() =>
                  queryClient.ensureQueryData(
                    keyAccountContractEstimatesExportQueryOptions(
                      selectedCrtPeriodStr,
                      rawSearch.manualInvestmentIds,
                    ),
                  )
                }
                rowCount={rows.length}
              />,
              <ExportCsvMenuItem
                key="final"
                label={t('keyAccountContractEstimates.export.final')}
                description={t('keyAccountContractEstimates.export.finalDescription')}
                getCsvExport={() =>
                  queryClient.ensureQueryData(
                    keyAccountContractEstimatesWithRatiosExportQueryOptions(
                      selectedCrtPeriodStr,
                      rawSearch.manualInvestmentIds,
                    ),
                  )
                }
                rowCount={rows.length}
              />,
            ]}
          />
          <PermissionsGuard required={['crt_key_account_contract_estimates_edit']}>
            <UploadKeyAccountEstimatesDataDialog crtPeriod={selectedCrtPeriodStr} />
          </PermissionsGuard>
          <Button type="button" variant="secondary" asChild>
            <Link
              to="/crt/customer-investments/key-accounts"
              search={{
                crtPeriod: selectedCrtPeriodStr,
                loadedVersionId: rawSearch.loadedVersionId,
                manualInvestmentIds: rawSearch.manualInvestmentIds,
              }}
            >
              <ArrowLeftIcon className="size-4" />
              {t('customerInvestments.actions.backToKeyAccounts')}
            </Link>
          </Button>
        </HStack>
      </VStack>

      {isPastPeriod && (
        <Note
          tone="warning"
          icon={InfoIcon}
          note={t('keyAccountContractEstimates.pastPeriod.note')}
        />
      )}

      <Card>
        <CardContent className="pt-6">
          {isEmpty ? (
            <EstimatesEmptyState
              crtPeriod={selectedCrtPeriodStr}
              onAddClick={() => setIsAddDialogOpen(true)}
            />
          ) : (
            <EstimatesTable
              rows={rows}
              isPending={isPending}
              productCategory={search.productCategory}
              crtPeriod={selectedCrtPeriodStr}
              localEdits={localEdits}
              onProductCategoryChange={handleProductCategoryChange}
              onCommitCell={handleCommitCell}
              onClearRow={handleClearRow}
              onEditKeyAccount={(row, canDelete) => setEditTarget({ row, canDelete })}
              onAddKeyAccount={() => setIsAddDialogOpen(true)}
              configurationAction={
                <ConfigureAssumptions
                  isOpen={isConfigOpen}
                  onOpenChange={setIsConfigOpen}
                  crtPeriod={selectedCrtPeriodStr}
                  kinds={['manual']}
                  selection={configuration}
                  onApply={handleApplyConfig}
                />
              }
              configBadges={configBadges}
              onConfigBadgeClick={() => setIsConfigOpen(true)}
              onEditPerformanceRatios={() => setIsRatiosDialogOpen(true)}
            />
          )}
        </CardContent>
      </Card>

      <PermissionsGuard required={['crt_key_account_contract_estimates_edit']}>
        <PendingChanges
          areChangesPending={hasUnsaved}
          isSavePending={isSaving}
          onSaveChanges={handleSaveChanges}
        />
      </PermissionsGuard>

      <AddKeyAccountDialog
        open={isAddDialogOpen}
        crtPeriod={selectedCrtPeriodStr}
        onOpenChange={setIsAddDialogOpen}
      />

      <EditPerformanceRatiosDialog
        open={isRatiosDialogOpen}
        crtPeriod={selectedCrtPeriodStr}
        onOpenChange={setIsRatiosDialogOpen}
      />

      {editTarget && (
        <EditKeyAccountDialog
          open
          onOpenChange={(next) => !next && setEditTarget(null)}
          hierarchyErp={editTarget.row.hierarchyErp}
          name={editTarget.row.name}
          accountType={editTarget.row.accountType}
          crtPeriod={selectedCrtPeriodStr}
          withDeleteButton={editTarget.canDelete}
        />
      )}
    </PageContainer>
  )
}
